// @flow
import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {Typography, IconButton, Grid} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {ArrowBack} from '@material-ui/icons';
import SortableList from 'react-easy-sort';

import {usePage} from '../../page/utils';

import HorizontalScroll from '../../../elements/HorizontalFilter/HorizontalScroll/HorizontalScroll';
import FilterButton from '../../../elements/HorizontalFilter/FilterButton/FilterButton';
import CustomButton from '../../../elements/CustomButton/CustomButton';
import AlertDueDateTier from '../../tiers/components/AlertDueDateTier/AlertDueDateTier';

import type {
  Store,
  User,
  Category,
  MinimalProduct,
  Currency,
  Pagination,
} from '../../../entities';
import {SIZE} from '../duck';

import {useDebounceEffect, useNearScreen} from '../../../utils/react';
import TiendadaTooltip from '../../../elements/tiendada-tooltip';
import Skeleton from '@material-ui/lab/Skeleton';
import {useSelector} from 'react-redux';
import {useToastActions} from '../../toast-alert/utils';
import {classNames} from '../../../utils/react/className';
import {colors} from '../../../assets/styles/colors';
import {useIsFeatureInTier} from '../../../utils/component';
import OrganizeProductItem from './OrganizeProductItem';
import InlineLoader from '../../../elements/LoadingSpinner/InlineLoader';

type Props = {
  store?: Store,
  user?: User,
  categories?: Category[],
  products?: MinimalProduct[],
  pagination?: Pagination,
  currency?: Currency,
  fetchOrganizedProducts: ({
    queryFilters?: {[key: string]: any},
    reset?: boolean,
  }) => void,
  sortProduct: ({
    sortRequest?: {[key: string]: any},
    categories?: string,
  }) => void,
  isFetching?: boolean,
  isSorting?: boolean,
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    background: '#F8F9FB',
    position: 'relative',
  },
  headerWrapper: {
    maxWidth: 1300,
    padding: '.75rem',
    background: '#f8f9fb',
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: 'black',
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  containerContent: {
    overflowY: 'hidden',
    padding: '0 1rem',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1300,
  },
  container: {
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
    borderRadius: '12px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1rem',
  },
  contentFilter: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  filter: {
    display: 'flex',
    alignItems: ' center',
    width: '100%',
  },
  allBoton: {
    marginRight: '0.7rem',
  },
  containerDragArea: {
    margin: '1rem 0',
  },
  scrollFlag: {
    height: '1px',
    opacity: 0,
    position: 'relative',
    display: 'block',
  },
  categoryDisabled: {
    opacity: 0.5,
  },
  sortableContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  '@global': {
    '.dragged': {
      transform: 'rotate(8deg)',
      boxShadow: '0px 8px 24px rgba(66, 64, 64, 0.12)',
      backgroundColor: colors.lightPurple,
      border: `1px solid ${colors.primary}`,
      zIndex: 1000,
    },
  },
  sortingBackdrop: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    backgroundColor: 'rgba(251, 251, 251, 0.75)',
    backdropFilter: 'blur(.5rem)',
    paddingTop: '35vh',
    gap: '1rem',
  },
  sortingText: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: colors.grey700,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem 1rem',
    color: colors.grey600,
    background: '#fff',
    borderRadius: 12,
    border: '1px dashed #E8E8ED',
  },
  sortingIndicator: {
    marginRight: '.5rem',
  },
}));

export default usePage<Props>(OrganizeProducts, 'Products Organize');

function OrganizeProducts(props: Props) {
  const {
    categories,
    products,
    fetchOrganizedProducts,
    sortProduct,
    isFetching,
    pagination,
    isSorting,
  } = props;

  const classes = useStyles();
  const {showToast} = useToastActions();
  const sortProductError = useSelector(
    state => state.organizeProducts.sortProductError
  );

  const {fromRef, isNearScreen} = useNearScreen({
    distance: '100px',
    once: false,
  });

  const [categoriesSelected, setCategoriesSelected] = useState([]);
  const [localProducts, setLocalProducts] = useState(products || []);

  const history = useHistory();
  const hasProductEditFeature = useIsFeatureInTier('PRODUCT_EDIT');

  const queryFilters = useMemo(() => {
    const params: {[key: string]: any} = {};
    if (categoriesSelected.length > 0) {
      params.categories = categoriesSelected.join(',');
    }
    return params;
  }, [categoriesSelected]);

  useEffect(() => {
    if (queryFilters) {
      fetchOrganizedProducts({queryFilters, reset: true});
    }
  }, [fetchOrganizedProducts, queryFilters]);

  useEffect(() => {
    if (products) setLocalProducts(products);
  }, [products]);

  const handleCategoryOption = useCallback(
    (id: string) => {
      categoriesSelected.indexOf(id) === -1
        ? setCategoriesSelected(prev => [...prev, id])
        : setCategoriesSelected(prev => prev.filter(e => e !== id));
    },
    [categoriesSelected]
  );

  const handleAllCategories = useCallback(() => {
    setCategoriesSelected([]);
  }, []);

  const handleAddPage = useCallback(() => {
    if (
      isFetching ||
      !products ||
      !pagination ||
      pagination.total <= SIZE ||
      products.length >= pagination.total
    ) {
      return;
    }
    if (!queryFilters) return;
    fetchOrganizedProducts({queryFilters, reset: false});
  }, [fetchOrganizedProducts, isFetching, pagination, products, queryFilters]);

  useDebounceEffect(
    () => {
      if (!isNearScreen) return;
      handleAddPage();
    },
    500,
    [handleAddPage, isNearScreen]
  );

  useEffect(() => {
    if (sortProductError) {
      showToast({
        type: 'error',
        title: 'Error al ordenar',
        message: sortProductError || 'Ocurrió un error al guardar el orden',
      });
    }
  }, [sortProductError, showToast]);

  const handleSortEnd = useCallback(
    (oldIndex: number, newIndex: number) => {
      if (oldIndex === newIndex || !hasProductEditFeature) return;

      const updated = [...localProducts];
      const [moved] = updated.splice(oldIndex, 1);
      updated.splice(newIndex, 0, moved);
      setLocalProducts(updated);

      const endId = localProducts[newIndex].id;

      const movement = {
        init: {pos: oldIndex, id: moved.id},
        end: {pos: newIndex, id: endId},
      };
      sortProduct({...queryFilters, sortRequest: movement});
    },
    [localProducts, hasProductEditFeature, sortProduct, queryFilters]
  );

  return (
    <div className={classes.root}>
      <AlertDueDateTier />

      <div className={classes.headerWrapper}>
        <Typography className={classes.title} id="organize-products-title">
          <IconButton
            className={classes.backButton}
            onClick={() => history.goBack()}
          >
            <ArrowBack />
          </IconButton>
          Ordenar Elementos
        </Typography>
      </div>

      <div className={classes.containerContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <main className={classes.content}>
              <div className={classes.container}>
                <div className={classes.contentFilter}>
                  <HorizontalScroll>
                    <div className={classes.filter}>
                      {isSorting && (
                        <div className={classes.sortingIndicator}>
                          <InlineLoader size={24} loading />
                        </div>
                      )}
                      <CustomButton
                        variant="contained"
                        className={classes.allBoton}
                        onClick={handleAllCategories}
                      >
                        Todos
                      </CustomButton>
                      {categories &&
                        categories.map((c: Category, index: number) => (
                          <TiendadaTooltip
                            key={c.id}
                            title={'Esta categoría está deshabilitada'}
                            disableHoverListener={c.enabled}
                            placement="top"
                          >
                            <div>
                              <FilterButton
                                key={index}
                                selected={
                                  categoriesSelected.indexOf(c.id) !== -1
                                }
                                id={c.id}
                                classes={{
                                  root: classNames({
                                    [classes.categoryDisabled]: !c.enabled,
                                  }),
                                }}
                                handleOnClick={handleCategoryOption}
                                text={c.name}
                              />
                            </div>
                          </TiendadaTooltip>
                        ))}
                    </div>
                  </HorizontalScroll>
                </div>
                <div className={classes.containerDragArea}>
                  {localProducts && localProducts.length > 0 && (
                    <SortableList
                      onSortEnd={handleSortEnd}
                      className={classes.sortableContainer}
                      draggedItemClassName="dragged"
                      lockAxis="y"
                    >
                      {localProducts.map((product: MinimalProduct) => (
                        <OrganizeProductItem
                          product={product}
                          key={product.id}
                        />
                      ))}
                    </SortableList>
                  )}

                  {!isFetching &&
                    (!localProducts || localProducts.length === 0) && (
                      <div className={classes.emptyState}>
                        <Typography
                          variant="subtitle1"
                          style={{fontWeight: 600}}
                        >
                          No hay resultados
                        </Typography>
                        <Typography variant="body2" style={{marginTop: 8}}>
                          No encontramos productos con los filtros
                          seleccionados.
                        </Typography>
                        {categoriesSelected.length > 0 && (
                          <CustomButton
                            style={{marginTop: 16}}
                            variant="outlined"
                            onClick={handleAllCategories}
                          >
                            Quitar filtros
                          </CustomButton>
                        )}
                      </div>
                    )}

                  {isFetching &&
                    Array.from({length: 1}, (x, i) => (
                      <Grid item xs={12} key={i}>
                        <Skeleton variant="text" height="100px" />
                      </Grid>
                    ))}

                  <span ref={fromRef} className={classes.scrollFlag} />
                </div>
              </div>
            </main>
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
